import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, Search, Sparkles, ChevronRight, MapPin, Clock, Bell, Loader2 } from "lucide-react";
import SEO from "../../components/SEO";
import SafeImage from "../../components/SafeImage";
import { api } from "../../lib/api";
import { SEGMENTS, track } from "../../lib/institutional";

const FAQ = {
  "onde-comer": [
    { q: "Quais os melhores bairros para comer em João Pessoa?", a: "Tambaú, Cabo Branco, Manaíra, Bessa e o Altiplano concentram grande parte dos restaurantes, bares e cafés da cidade." },
    { q: "Como encontrar frutos do mar frescos?", a: "Filtre por restaurantes na orla e pergunte à IA por casas especializadas em frutos do mar em Tambaú e Cabo Branco." },
  ],
  hoteis: [{ q: "Há hotéis à beira-mar em João Pessoa?", a: "Sim, especialmente em Tambaú, Cabo Branco e Bessa. Veja as opções cadastradas e fale direto pelo WhatsApp." }],
  praias: [{ q: "Qual a praia mais famosa de João Pessoa?", a: "Tambaú e Cabo Branco são as mais conhecidas; o Farol do Cabo Branco marca o ponto mais oriental das Américas." }],
};

export default function SegmentPage({ slug }) {
  const seg = SEGMENTS.find((s) => s.slug === slug);
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [email, setEmail] = useState("");
  const [notified, setNotified] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!seg || seg.comingSoon) return;
    const isBiz = seg.href.startsWith("/empresas");
    const url = isBiz ? `/businesses?category=${seg.category}&limit=6` : `/feed?category=${seg.category}&limit=6`;
    api.get(url).then(({ data }) => setItems((data.items || data || []).slice(0, 6))).catch(() => {});
    track("segment_view", { slug });
  }, [slug, seg]);

  if (!seg) return null;

  if (seg.comingSoon) {
    const notifyMe = async (e) => {
      e.preventDefault();
      if (!email) return;
      setSaving(true);
      try {
        await api.post("/contact", { name: "Lista de espera", email, subject: `Waitlist: ${seg.label}`, message: `Avise-me quando ${seg.label} estiver disponível.` });
        track("waitlist_join", { segment: seg.slug });
        setNotified(true); setEmail("");
        toast.success("Pronto! Avisaremos você quando estiver disponível.");
      } catch { toast.error("Não foi possível cadastrar. Tente novamente."); }
      finally { setSaving(false); }
    };
    return (
      <div data-testid={`segment-comingsoon-${seg.slug}`}>
        <SEO title={`${seg.title} — Em breve`} description={seg.desc} path={`/${seg.slug}`} image={seg.image} />
        <section className="relative min-h-[80vh] flex items-center">
          <SafeImage src={seg.image} category={seg.category} alt={seg.h1} className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-slate-900/75" />
          <div className="relative max-w-2xl mx-auto px-4 md:px-8 text-center text-white">
            <span className="inline-flex items-center gap-2 rounded-full bg-white/15 backdrop-blur px-4 py-1.5 text-xs font-semibold"><Clock className="w-4 h-4" /> EM DESENVOLVIMENTO</span>
            <h1 className="mt-5 font-heading font-extrabold text-4xl md:text-5xl">{seg.label} em João Pessoa</h1>
            <p className="mt-4 text-white/85 text-lg">Estamos preparando uma nova experiência.</p>
            {notified ? (
              <div data-testid="waitlist-done" className="mt-8 inline-flex items-center gap-2 rounded-full bg-success/20 border border-success/40 px-5 py-3 text-white">
                <Bell className="w-5 h-5" /> Avisaremos você em primeira mão!
              </div>
            ) : (
              <form onSubmit={notifyMe} data-testid="waitlist-form" className="mt-8 max-w-md mx-auto flex flex-col sm:flex-row gap-2 bg-white rounded-2xl sm:rounded-full p-2">
                <input data-testid="waitlist-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Seu melhor e-mail" className="flex-1 px-4 py-3 rounded-xl sm:rounded-full text-slate-800 outline-none" />
                <button data-testid="waitlist-submit" type="submit" disabled={saving} className="px-5 py-3 rounded-xl sm:rounded-full bg-brand-gradient text-white font-semibold press inline-flex items-center justify-center gap-2 disabled:opacity-60">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bell className="w-4 h-4" />} Avise-me
                </button>
              </form>
            )}
            <div className="mt-6">
              <button onClick={() => navigate("/feed")} className="text-white/80 hover:text-white text-sm font-semibold inline-flex items-center gap-1">Explorar o resto do app <ArrowRight className="w-4 h-4" /></button>
            </div>
          </div>
        </section>
      </div>
    );
  }

  const isBiz = seg.href.startsWith("/empresas");

  const faqs = FAQ[seg.category] || [];
  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      { "@type": "BreadcrumbList", itemListElement: [
        { "@type": "ListItem", position: 1, name: "Início", item: "https://guiadejampa.com/" },
        { "@type": "ListItem", position: 2, name: seg.title, item: `https://guiadejampa.com/${seg.slug}` },
      ] },
      ...(faqs.length ? [{ "@type": "FAQPage", mainEntity: faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })) }] : []),
    ],
  };

  return (
    <div data-testid={`segment-${seg.slug}`}>
      <SEO title={seg.title} description={seg.desc} path={`/${seg.slug}`} image={seg.image} jsonLd={jsonLd}
        keywords={`${seg.title}, João Pessoa PB, ${seg.label} em João Pessoa, guia de Jampa`} />

      {/* Hero */}
      <section className="relative h-[60vh] min-h-[420px] flex items-end">
        <SafeImage src={seg.image} category={seg.category} alt={seg.h1} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/85 via-slate-900/45 to-slate-900/40" />
        <div className="relative max-w-7xl mx-auto px-4 md:px-8 pb-12 w-full text-white">
          <p className="text-xs font-semibold tracking-widest uppercase text-white/80 mb-2">Guia de Jampa AI · João Pessoa</p>
          <h1 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl max-w-3xl">{seg.h1}</h1>
          <button data-testid="segment-cta" onClick={() => { track("segment_open_app", { slug }); navigate(seg.href); }} className="mt-6 px-5 py-3 rounded-full bg-brand-gradient text-white font-semibold premium-shadow press inline-flex items-center gap-2">
            {isBiz ? "Ver no app" : "Explorar no app"} <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </section>

      {/* Intro */}
      <section className="max-w-3xl mx-auto px-4 md:px-8 py-14">
        {seg.intro.map((p) => <p key={p} className="text-slate-600 leading-relaxed mb-4 text-[15px]">{p}</p>)}
      </section>

      {/* Live items */}
      {items.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 md:px-8 pb-14">
          <h2 className="font-heading font-bold text-2xl text-slate-900 mb-6">Em destaque agora</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {items.map((it) => (
              <button key={it.id || it.slug} onClick={() => navigate(isBiz ? `/empresa/${it.slug}` : seg.href)}
                className="text-left rounded-2xl overflow-hidden bg-white soft-shadow hover:-translate-y-1 transition-transform">
                <div className="relative h-40"><SafeImage src={it.cover || it.image} category={seg.category} alt={it.name || it.title} loading="lazy" className="w-full h-full object-cover" /></div>
                <div className="p-5">
                  <p className="font-heading font-bold text-slate-900 line-clamp-2">{it.name || it.title}</p>
                  {(it.neighborhood || it.address) && <p className="text-sm text-slate-500 flex items-center gap-1 mt-1"><MapPin className="w-3.5 h-3.5" /> {it.neighborhood || it.address}</p>}
                  <span className="inline-flex items-center gap-1 text-sm font-semibold text-primary mt-3">Ver mais <ChevronRight className="w-4 h-4" /></span>
                </div>
              </button>
            ))}
          </div>
        </section>
      )}

      {/* FAQ */}
      {faqs.length > 0 && (
        <section className="max-w-3xl mx-auto px-4 md:px-8 pb-16">
          <h2 className="font-heading font-bold text-2xl text-slate-900 mb-6">Perguntas frequentes</h2>
          <div className="space-y-4">
            {faqs.map((f) => (
              <div key={f.q} className="bg-slate-50 rounded-2xl p-5">
                <p className="font-heading font-semibold text-slate-900">{f.q}</p>
                <p className="text-slate-600 text-sm mt-2">{f.a}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 pb-20">
        <div className="rounded-3xl bg-brand-gradient text-white p-10 text-center">
          <h2 className="font-heading font-extrabold text-2xl md:text-3xl">Descubra {seg.label.toLowerCase()} com a IA de João Pessoa</h2>
          <p className="text-white/85 mt-2">Pergunte, filtre e receba recomendações com dados reais da cidade.</p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <button onClick={() => navigate(seg.href)} className="px-6 py-3 rounded-full bg-white text-slate-900 font-semibold press inline-flex items-center gap-2"><Search className="w-4 h-4" /> Abrir no app</button>
            <button onClick={() => navigate("/ia")} className="px-6 py-3 rounded-full border border-white/50 text-white font-semibold press inline-flex items-center gap-2"><Sparkles className="w-4 h-4" /> Perguntar à IA</button>
          </div>
        </div>
      </section>
    </div>
  );
}
